
import { supabase } from '@/lib/supabase';
import CryptoJS from 'crypto-js';

// Default cache TTLs in seconds
export const DEFAULT_CACHE_TTL = {
  EMBEDDING: 7 * 24 * 60 * 60, // 7 days for embeddings
  RESPONSE: 24 * 60 * 60,      // 24 hours for responses
  KNOWLEDGE: 30 * 24 * 60 * 60 // 30 days for knowledge
};

/**
 * Generate a content hash from text for cache lookup
 */
export const generateContentHash = (text: string, options: Record<string, any> = {}): string => {
  // Combine text with any options that might affect the result
  const hashContent = JSON.stringify({
    text,
    ...options
  });
  
  return CryptoJS.SHA256(hashContent).toString(CryptoJS.enc.Hex);
};

/**
 * Check if an embedding exists in cache
 */
export const getEmbeddingFromCache = async (
  text: string, 
  model: string = 'text-embedding-3-small'
): Promise<number[] | null> => {
  try {
    const contentHash = generateContentHash(text, { model });
    
    const { data, error } = await supabase
      .from('embedding_cache')
      .select('embedding')
      .eq('content_hash', contentHash)
      .gt('expires_at', new Date().toISOString())
      .single();
    
    if (error || !data) {
      return null;
    }
    
    return data.embedding;
  } catch (error) {
    console.error('Error checking embedding cache:', error);
    return null;
  }
};

/**
 * Store an embedding in cache
 */
export const storeEmbeddingInCache = async (
  text: string,
  embedding: number[],
  model: string = 'text-embedding-3-small',
  ttlSeconds: number = DEFAULT_CACHE_TTL.EMBEDDING
): Promise<boolean> => {
  try {
    const contentHash = generateContentHash(text, { model });
    const expiresAt = new Date();
    expiresAt.setSeconds(expiresAt.getSeconds() + ttlSeconds);
    
    const { error } = await supabase
      .from('embedding_cache')
      .upsert({
        content_hash: contentHash,
        embedding,
        model,
        expires_at: expiresAt.toISOString()
      }, {
        onConflict: 'content_hash'
      });
    
    return !error;
  } catch (error) {
    console.error('Error storing embedding in cache:', error);
    return false;
  }
};

/**
 * Check if a similar response exists in cache
 */
export const getResponseFromCache = async (
  query: string,
  queryEmbedding: number[],
  topic: string,
  language: string = 'en',
  similarityThreshold: number = 0.95
): Promise<{ response: string, suggestions: string[] } | null> => {
  try {
    // Try exact match first using hash (now includes topic in hash)
    const queryHash = generateContentHash(query, { topic, language });
    
    const { data: exactMatch, error: exactError } = await supabase
      .from('response_cache')
      .select('response, suggestions')
      .eq('query_hash', queryHash)
      .eq('topic', topic)
      .eq('language', language)
      .gt('expires_at', new Date().toISOString())
      .single();
    
    if (exactMatch) {
      return {
        response: exactMatch.response,
        suggestions: exactMatch.suggestions || []
      };
    }
    
    // If no exact match, try similarity search (filtered by topic)
    const { data: similarMatches, error: similarError } = await supabase.rpc(
      'match_response_cache',
      {
        query_embedding: queryEmbedding,
        match_threshold: similarityThreshold,
        match_count: 1,
        filter_topic: topic,
        filter_language: language
      }
    );
    
    if (similarError || !similarMatches || similarMatches.length === 0) {
      return null;
    }
    
    return {
      response: similarMatches[0].response,
      suggestions: similarMatches[0].suggestions || []
    };
  } catch (error) {
    console.error('Error checking response cache:', error);
    return null;
  }
};

/**
 * Store a response in cache
 */
export const storeResponseInCache = async (
  query: string,
  queryEmbedding: number[],
  topic: string,
  language: string,
  response: string,
  suggestions: string[] = [],
  ttlSeconds: number = DEFAULT_CACHE_TTL.RESPONSE
): Promise<boolean> => {
  try {
    const queryHash = generateContentHash(query, { topic, language });
    const expiresAt = new Date();
    expiresAt.setSeconds(expiresAt.getSeconds() + ttlSeconds);
    
    const { error } = await supabase
      .from('response_cache')
      .upsert({
        query_hash: queryHash,
        query_embedding: queryEmbedding,
        query_text: query,
        topic,
        language,
        response,
        suggestions,
        expires_at: expiresAt.toISOString()
      }, {
        onConflict: 'query_hash'
      });
    
    return !error;
  } catch (error) {
    console.error('Error storing response in cache:', error);
    return false;
  }
};

/**
 * Invalidate cache entries by topic
 */
export const invalidateCacheByTopic = async (topic: string): Promise<boolean> => {
  try {
    const { error: responseError } = await supabase
      .from('response_cache')
      .delete()
      .eq('topic', topic);
      
    return !responseError;
  } catch (error) {
    console.error('Error invalidating cache:', error);
    return false;
  }
};

/**
 * Get cache statistics
 */
export const getCacheStats = async (): Promise<{
  embeddingCount: number;
  responseCount: number;
  embeddingSize: string;
  responseSize: string;
}> => {
  try {
    const { count: embeddingCount } = await supabase
      .from('embedding_cache')
      .select('*', { count: 'exact', head: true });
      
    const { count: responseCount } = await supabase
      .from('response_cache')
      .select('*', { count: 'exact', head: true });
      
    // Simple estimation of size based on average embedding size
    const embeddingSize = ((embeddingCount || 0) * 1536 * 4 / (1024 * 1024)).toFixed(2) + ' MB';
    const responseSize = ((responseCount || 0) * 1.5 / 1024).toFixed(2) + ' MB';
    
    return {
      embeddingCount: embeddingCount || 0,
      responseCount: responseCount || 0,
      embeddingSize,
      responseSize
    };
  } catch (error) {
    console.error('Error getting cache stats:', error);
    return {
      embeddingCount: 0,
      responseCount: 0,
      embeddingSize: '0 MB',
      responseSize: '0 MB'
    };
  }
};
